import { FIELD_UPDATE_SOURCE } from "@common/constants";
import { BillExtractionRecord } from "@common/schemas/financialDocument/bill-extraction-record.schema";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";

export interface PersistedInvoiceExtractionLineItem {
    id: string;
    rowIndex: number;
    item: string | null;
    description: string | null;
    amount: number;
    quantity: number | null;
    unitPrice: number | null;
    classIds: string[] | null;
    accountId: string | null;
    accrualAccountId: string | null;
    accountIdSource: FIELD_UPDATE_SOURCE | null;
    catalogItemId: string | null;
    catalogItemPriceId: string | null;
}

export interface PersistedInvoiceExtraction {
    id: string;
    orderIndex?: number;
    counterpartyName: string | null;
    counterpartyId: string | null;
    counterpartyIdSource: FIELD_UPDATE_SOURCE | null;
    documentNumber: string | null;
    documentDate: string | null;
    dueDate: string | null;
    totalAmount: number | null;
    taxAmount: number | null;
    discountAmount: number | null;
    depositAmount: number | null;
    memo: string | null;
    balanceDue: number | null;
    paymentDate: string | null;
    entityId: number | null;
    warnings: string[] | null;
    lineItems?: PersistedInvoiceExtractionLineItem[];
}

export interface PersistedBillExtractionLineItem {
    id: string;
    rowIndex: number;
    item: string | null;
    description: string | null;
    amount: number;
    quantity: number | null;
    unitPrice: number | null;
    tax?: number | null;
    classIds: string[] | null;
    accountId: string | null;
    accrualAccountId: string | null;
    accountIdSource: FIELD_UPDATE_SOURCE | null;
}

export interface PersistedBillExtraction {
    id: string;
    orderIndex?: number;
    counterpartyName: string | null;
    counterpartyId: string | null;
    counterpartyIdSource: FIELD_UPDATE_SOURCE | null;
    documentNumber: string | null;
    documentDate: string | null;
    dueDate: string | null;
    totalAmount: number | null;
    taxAmount: number | null;
    discountAmount: number | null;
    depositAmount: number | null;
    memo: string | null;
    balanceDue: number | null;
    paymentDate: string | null;
    entityId: number | null;
    warnings: string[] | null;
    lineItems?: PersistedBillExtractionLineItem[];
}

export interface InvoiceExtractionsFromPersistedParams {
    invoiceExtractions: PersistedInvoiceExtraction[];
}

export interface BillExtractionsFromPersistedParams {
    billExtractions: PersistedBillExtraction[];
}

interface PersistedAccrualExtractionLineItem {
    id: string;
    rowIndex: number;
    item?: string | null;
    description: string | null;
    amount: number;
    quantity?: number | null;
    unitPrice?: number | null;
    tax?: number | null;
    classIds: string[] | null;
    accountId: string | null;
    accrualAccountId: string | null;
    accountIdSource?: FIELD_UPDATE_SOURCE | null;
}

function mapPersistedLineItem(lineItem: PersistedAccrualExtractionLineItem) {
    return {
        lineId: lineItem.id,
        item: lineItem.item ?? null,
        description: lineItem.description,
        amount: lineItem.amount,
        quantity: lineItem.quantity ?? null,
        unitPrice: lineItem.unitPrice ?? null,
        tax: lineItem.tax ?? null,
        classIds: lineItem.classIds,
        accountId: lineItem.accountId,
        accrualAccountId: lineItem.accrualAccountId,
        accountIdSource: lineItem.accountIdSource ?? null,
    };
}

function mapPersistedInvoiceLineItem(
    lineItem: PersistedInvoiceExtractionLineItem,
) {
    return {
        ...mapPersistedLineItem(lineItem),
        catalogItemId: lineItem.catalogItemId ?? null,
        catalogItemPriceId: lineItem.catalogItemPriceId ?? null,
    };
}

export function invoiceExtractionsFromPersisted({
    invoiceExtractions,
}: InvoiceExtractionsFromPersistedParams): InvoiceExtractionRecord[] {
    return [...invoiceExtractions]
        .sort((left, right) => (left.orderIndex ?? 0) - (right.orderIndex ?? 0))
        .map((extraction) => ({
            id: extraction.id,
            counterpartyName: extraction.counterpartyName,
            counterpartyId: extraction.counterpartyId,
            counterpartyIdSource: extraction.counterpartyIdSource,
            documentNumber: extraction.documentNumber,
            documentDate: extraction.documentDate,
            dueDate: extraction.dueDate,
            totalAmount: extraction.totalAmount,
            taxAmount: extraction.taxAmount,
            discountAmount: extraction.discountAmount,
            depositAmount: extraction.depositAmount,
            memo: extraction.memo,
            balanceDue: extraction.balanceDue,
            paymentDate: extraction.paymentDate,
            entityId: extraction.entityId,
            warnings: extraction.warnings,
            lineItems: [...(extraction.lineItems ?? [])]
                .sort((left, right) => left.rowIndex - right.rowIndex)
                .map(mapPersistedInvoiceLineItem),
        }));
}

export function billExtractionsFromPersisted({
    billExtractions,
}: BillExtractionsFromPersistedParams): BillExtractionRecord[] {
    return [...billExtractions]
        .sort((left, right) => (left.orderIndex ?? 0) - (right.orderIndex ?? 0))
        .map((extraction) => ({
            id: extraction.id,
            counterpartyName: extraction.counterpartyName,
            counterpartyId: extraction.counterpartyId,
            counterpartyIdSource: extraction.counterpartyIdSource,
            documentNumber: extraction.documentNumber,
            documentDate: extraction.documentDate,
            dueDate: extraction.dueDate,
            totalAmount: extraction.totalAmount,
            taxAmount: extraction.taxAmount,
            discountAmount: extraction.discountAmount,
            depositAmount: extraction.depositAmount,
            memo: extraction.memo,
            balanceDue: extraction.balanceDue,
            paymentDate: extraction.paymentDate,
            entityId: extraction.entityId,
            warnings: extraction.warnings,
            lineItems: [...(extraction.lineItems ?? [])]
                .sort((left, right) => left.rowIndex - right.rowIndex)
                .map(mapPersistedLineItem),
        }));
}
